import { useMemo, useState } from "react";
import { Input, message } from "antd";
import qrScanIcon from "../../assets/images/Qrscan-Icon.png";
import { Button2, Select } from "../index";
import { qrPaymentsApi } from "../../api/endpoints";
import { useAccounts } from "../../data/live";
import { newIdempotencyKey } from "../../utils/idempotency";

const QrPay = () => {
  const { accounts, loading: accountsLoading } = useAccounts();
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [amount, setAmount] = useState("");
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === sourceAccountId) || accounts[0],
    [accounts, sourceAccountId]
  );

  const accountOptions = accounts.map((account) => ({
    value: account.id,
    name: `${account.currency} ${account.accountNumber || account.id}`,
  }));

  const parseQr = async () => {
    setError(null);
    setParsed(null);
    if (!qrValue.trim()) return setError("Paste or scan a QR payment payload");
    setLoading(true);
    try {
      const result = await qrPaymentsApi.parse({ qrValue });
      setParsed(result);
    } catch (err) {
      setError(err.response?.data?.error || "QR payload could not be parsed");
    } finally {
      setLoading(false);
    }
  };

  const pay = async () => {
    setError(null);
    const targetAccountId = sourceAccountId || selectedAccount?.id;
    if (!targetAccountId) return setError("Select a source wallet");
    if (!qrValue.trim()) return setError("Paste or scan a QR payment payload");
    if (!amount || Number(amount) <= 0) return setError("Enter a valid amount");

    setLoading(true);
    try {
      await qrPaymentsApi.create(
        {
          sourceAccountId: targetAccountId,
          qrValue,
          amount: Number(amount),
        },
        idempotencyKey
      );
      message.success("QR payment created");
      setQrValue("");
      setAmount("");
      setParsed(null);
      setIdempotencyKey(newIdempotencyKey());
    } catch (err) {
      setError(err.response?.data?.error || "QR payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column align-items-center justify-content-center gap-3 h-100 py-4">
        <div>
          <img src={qrScanIcon} alt="icon" />
        </div>
        <span className="fs-5 fw-bold text-center">QR Payment</span>
        <span
          className="fs14px fw-medium text-center"
          style={{ color: "#7D7D7D", maxWidth: 420 }}
        >
          Paste a PayID or bank-transfer QR payload. The backend parses it and
          creates a normal transfer from your wallet.
        </span>

        <div className="d-flex flex-column gap-3 w-75" style={{ maxWidth: 520 }}>
          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Source wallet</label>
            <Select
              defaultValue={accountsLoading ? "Loading wallets" : "Select wallet"}
              value={sourceAccountId || selectedAccount?.id || ""}
              options={accountOptions}
              className="p-3"
              onChange={(e) => setSourceAccountId(e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">QR payload</label>
            <Input.TextArea
              className="p-3"
              autoSize={{ minRows: 3, maxRows: 5 }}
              placeholder="Paste PayID or bank details from QR"
              value={qrValue}
              onChange={(e) => setQrValue(e.target.value)}
            />
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="fs12px">Amount</label>
            <Input
              className="p-3"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              suffix={parsed?.currency || selectedAccount?.currency || ""}
            />
          </div>

          {parsed && (
            <div className="border rounded-3 p-3 fs14px">
              <div className="d-flex justify-content-between">
                <span style={{ color: "#7D7D7D" }}>Type</span>
                <span className="fw-semibold">{parsed.type}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span style={{ color: "#7D7D7D" }}>Reference</span>
                <span className="fw-semibold">{parsed.reference}</span>
              </div>
            </div>
          )}

          {error && (
            <span className="fs12px" style={{ color: "#FC555B" }}>
              {error}
            </span>
          )}

          <div className="custom-div d-flex gap-2 justify-content-end">
            <Button2
              buttonName={loading ? "Checking..." : "Parse QR"}
              className={"btn-outline-success"}
              onClick={parseQr}
              disabled={loading}
            />
            <Button2
              buttonName={loading ? "Creating..." : "Pay"}
              className={"btn-dark"}
              onClick={pay}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default QrPay;
